import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import CustomRanking from '../models/iCustomRanking';
import { useLogger } from '../hooks/useLogger';
import ProductTypes from '../enum/ProductTypesEnum';

interface IEditCustomRankingDialogProps {
  open: boolean;
  ranking: CustomRanking | null;
  onClose: () => void;
  onSave: (ranking: CustomRanking) => Promise<boolean>;
}

const productTypeOptions = Object.values(ProductTypes);

export const EditCustomRankingDialog = ({ open, ranking, onClose, onSave }: IEditCustomRankingDialogProps) => {
  const { authState } = useOktaAuth(); // Get auth state for current user
  const logger = useLogger('EditCustomRankingDialog');
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormState] = useState<CustomRanking | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (ranking) {
      // When the dialog opens, populate the form with the ranking data
      // and overwrite 'modifiedBy' with the current user's email.
      setFormState({
        ...ranking,
        modifiedBy: authState?.idToken?.claims.email || ranking.modifiedBy,
      });
    }
  }, [ranking, authState]);

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    if (!formState) return false;
    const tempErrors: { [key: string]: string } = {};
    if (!formState.rank) {
      tempErrors.rank = 'Rank is required.';
    } else if (!/^\d+$/.test(formState.rank.toString())) {
      tempErrors.rank = 'Rank must be an integer.';
    }
    if (!formState.productType || !formState.productType.trim()) {
      tempErrors.productType = 'Product Type is required.';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !formState) return;

    setIsSaving(true);
    try {
      const success = await onSave({
        ...formState,
        rank: Number(formState.rank),
      });

      if (success) {
        handleClose();
      }
    } catch (error) {
      logger.UI.error('Update operation failed', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAutocompleteChange = (newValue: string | null) => {
    if (!formState) return;

    setFormState({
      ...formState,
      // Set to the selected value, or an empty string if cleared
      productType: newValue || '',
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formState) return;
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Custom Ranking</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Read-only fields */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Sales Org"
                value={formState?.salesOrg || ''}
                variant="filled"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Product Y Number"
                value={formState?.productYNumber || ''}
                variant="filled"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            {/* Editable field */}
            <Grid size={12}>
              <Autocomplete
                fullWidth
                options={productTypeOptions}
                value={formState?.productType || null}
                onChange={(_, newValue) => {
                  handleAutocompleteChange(newValue);
                }}
                disabled={isSaving}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Product Type"
                    error={!!errors.productType}
                    helperText={errors.productType}
                  />
                )}
              />
            </Grid>
            {/* Editable field */}
            <Grid size={12}>
              <TextField
                fullWidth
                required
                name="rank"
                label="Rank"
                type="number"
                value={formState?.rank || ''}
                onChange={handleChange}
                error={!!errors.rank}
                helperText={errors.rank}
                disabled={isSaving}
              />
            </Grid>
            {/* Auto-populated, non-editable field */}
            <Grid size={12}>
              <TextField
                fullWidth
                name="modifiedBy"
                label="Modified By"
                value={formState?.modifiedBy || ''}
                variant="filled"
                InputProps={{ readOnly: true }}
                disabled={isSaving}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
